import { useContext, useState } from "react";
import { useQueryClient } from "react-query";

import { axiosInstance } from "api/axios";
import useDocumentEditor from "hooks/useDocumentEditor";
import officialLettersService, { officialLettersQueryKeys } from "api/officialLettersService";
import ConfirmModal from "components/ConfirmModal";
import ModalsManagerContext from "contexts/ModalsManager";
import KeysContext from "contexts/KeysContext";
import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";
import { IOfficialLettersActionResponse } from "dto/OfficialLetters/IOfficialLetters";
import { ENotificationType, notification } from "utils/notification";
import { radixErrorText } from "modules/radixModule/radixUtils";

export interface LetterDocRef {
  DocumentId: string;
  Title?: string | null;
  CaseId?: string | null;
  EncryptionKeyId?: string | null;
}

/** Pytanie przed akcją — pokazywane we wspólnym ConfirmModal (jak inne komunikaty aplikacji). */
export interface LetterConfirm {
  title: string;
  text: string;
  acceptLabel: string;
}

type ActionMutate = ReturnType<typeof officialLettersService.useSetReadyToDispatch>["mutate"];

// Akcje wspólne dla „Projektów pism" i „Wychodzących pism". Lista renderuje `confirmModal`.
const useLetterActions = () => {
  const queryClient = useQueryClient();
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { keys, setKeys } = useContext(KeysContext);
  const { openEditor } = useDocumentEditor();

  const [pending, setPending] = useState<(LetterConfirm & { proceed: () => void }) | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries(officialLettersQueryKeys.all);
    queryClient.invalidateQueries(["outgoing.categories"]);
    queryClient.invalidateQueries(["outgoing.dispatchList"]);
    queryClient.invalidateQueries(["outgoing.byDocument"]);
  };

  const openInEditor = async (doc: LetterDocRef) => {
    await openEditor({ Id: doc.DocumentId, Kind: ItemKind.EditorDocument });
  };

  /** Klucz dokumentu → KeysContext (kreator potrzebuje go przy „Przekaż do podpisu"). */
  const ensureDocumentKey = async (keyId?: string | null) => {
    if (!keyId || keys.some((k) => k?.Id === keyId)) return;
    try {
      const res = await axiosInstance.get(`/keys/object?id=${keyId}`);
      // Serwer dokłada ParentPrivateKey/ParentKeyId poza kształtem IKey.
      const key = res.data as IKey & { ParentPrivateKey?: string | null; ParentKeyId?: string | null };
      const parentKey: IKey | null = key?.ParentPrivateKey
        ? {
            Id: key.ParentKeyId ?? "",
            ParentKeyId: null,
            PrivateKey: key.ParentPrivateKey,
            PublicKey: "",
            Initial: false,
            Armored: false,
          }
        : null;
      setKeys([...keys, key, parentKey].filter(Boolean) as IKey[]);
    } catch {
      // Kreator sam zgłosi brak klucza, jeśli będzie potrzebny.
    }
  };

  /** Kreator „Generuj pisma" (Magistrat: „Podpisz") — czyta dokument z localStorage("package"). */
  const generateLetters = async (doc: LetterDocRef) => {
    await ensureDocumentKey(doc.EncryptionKeyId);
    localStorage.setItem(
      "package",
      JSON.stringify({
        Id: doc.DocumentId,
        Case: doc.CaseId ? { Id: doc.CaseId } : null,
        EncryptionKeyId: doc.EncryptionKeyId,
        S1: doc.Title,
      }),
    );
    setModals({ ...modals, generateLetters: true, hide: false });
  };

  const execute = (mutate: ActionMutate, documentIds: string[], title: string, onDone?: () => void) =>
    mutate(
      { DocumentIds: documentIds },
      {
        onSuccess: (res: { data: IOfficialLettersActionResponse }) => {
          const r = res.data;
          notification({
            type: r.Affected > 0 ? ENotificationType.SUCCESS : ENotificationType.INFO,
            title: r.Affected > 0 ? title : "Nic nie zmieniono",
            text:
              r.Skipped > 0
                ? r.Message ?? `Pominięto: ${r.Skipped}`
                : `Zmieniono: ${r.Affected}`,
          });
          invalidate();
          onDone?.();
        },
        onError: (e: unknown) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: radixErrorText(e, "Operacja nie powiodła się."),
          }),
      },
    );

  const run = (
    mutate: ActionMutate,
    documentIds: string[],
    title: string,
    confirm?: LetterConfirm,
    onDone?: () => void,
  ) => {
    if (documentIds.length === 0) return;
    const proceed = () => execute(mutate, documentIds, title, onDone);
    if (confirm) setPending({ ...confirm, proceed });
    else proceed();
  };

  const confirmModal = pending ? (
    <ConfirmModal
      title={pending.title}
      text={pending.text}
      acceptLabel={pending.acceptLabel}
      cancelLabel="Anuluj"
      showDontShowNextTime={false}
      onAccept={() => {
        setPending(null);
        pending.proceed();
      }}
      onCancel={() => setPending(null)}
    />
  ) : null;

  return { invalidate, openInEditor, generateLetters, run, confirmModal };
};

export default useLetterActions;
